import { z } from "zod";
import { CatalogSlug, Sha256, Username } from "../../common";

export const CatalogBlobParams = z.strictObject({
  sha256: Sha256,
});
export type CatalogBlobParams = z.infer<typeof CatalogBlobParams>;

/** The largest file one presigned `PUT` takes. A bigger one is sent in parts. */
export const CATALOG_SINGLE_UPLOAD_MAX_BYTES = 5 * 1024 ** 3;
/** The size of every part of a file sent in parts, except the last. */
export const CATALOG_UPLOAD_PART_BYTES = 512 * 1024 ** 2;
/** The largest file a catalog publisher can put. */
const CATALOG_BLOB_MAX_BYTES = 64 * 1024 ** 3;

export const PutCatalogBlobRequest = z
  .strictObject({
    bytes: z.int().positive().max(CATALOG_BLOB_MAX_BYTES).meta({
      description: "The file's size.",
      example: 48_211_337,
    }),
    contentType: z.string().min(1).max(200).meta({
      description: "What the file is.",
      example: "application/vnd.pmtiles",
    }),
  })
  .meta({
    id: "PutCatalogBlobRequest",
    description: "The file a catalog publisher is about to send.",
  });
export type PutCatalogBlobRequest = z.infer<typeof PutCatalogBlobRequest>;

const SingleUpload = z
  .strictObject({
    kind: z.literal("single"),
    url: z.url(),
    method: z.literal("PUT"),
    headers: z.record(z.string(), z.string()).meta({
      description:
        "Send exactly these headers with the `PUT`, including the SHA-256 storage checks the bytes against.",
    }),
    expiresAt: z.iso.datetime(),
  })
  .meta({ id: "CatalogSingleUpload", description: "One presigned `PUT` for the whole file." });

const PartedUpload = z
  .strictObject({
    kind: z.literal("parts"),
    partBytes: z.int().positive().meta({ description: "Every part's size, except the last." }),
    parts: z
      .array(z.strictObject({ partNumber: z.int().positive(), url: z.url() }))
      .min(1)
      .meta({ description: "A presigned `PUT` for each part, in order." }),
    complete: z.strictObject({ url: z.url(), method: z.literal("POST") }).meta({
      description:
        "Where to send `CompleteMultipartUpload` with each part's `ETag` once every part is sent.",
    }),
    expiresAt: z.iso.datetime(),
  })
  .meta({ id: "CatalogPartedUpload", description: "Presigned parts, for a file over 5 GiB." });

export const CatalogUpload = z.discriminatedUnion("kind", [SingleUpload, PartedUpload]);
export type CatalogUpload = z.infer<typeof CatalogUpload>;

export const PutCatalogBlobResponse = z
  .strictObject({
    stored: z.boolean().meta({
      description:
        "Whether the environment already has the file, stored or waiting in the uploads bucket, so there's nothing to send.",
    }),
    upload: CatalogUpload.optional().meta({ description: "Otherwise, where to send it." }),
  })
  .meta({ id: "PutCatalogBlobResponse", description: "Whether to send the file, and where." });
export type PutCatalogBlobResponse = z.infer<typeof PutCatalogBlobResponse>;

const CATALOG_KINDS = ["preset", "image", "video", "audio", "basemap", "agent", "eval"] as const;

export const CatalogKind = z.enum(CATALOG_KINDS).meta({
  id: "CatalogKind",
  description: "What a catalog item is.",
  example: "basemap",
});
export type CatalogKind = z.infer<typeof CatalogKind>;

/** A file's role in a version, such as `manifest`, `world`, or a basemap's `bundle:cover`. */
const CatalogFileRole = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9_:-]*$/);

export const PublishCatalogVersionRequest = z
  .strictObject({
    kind: CatalogKind,
    id: z.uuid().optional().meta({
      description:
        "The item's id, for the version that creates it. The seed derives it from the slug, so an item is the same row in every environment.",
    }),
    versionId: z.uuid().optional().meta({
      description:
        "The id the version is written with, for a picture, video, or recording, which worlds name by version. The seed derives it from the item's id and the original's hash, so the same original is the same version in every environment.",
    }),
    slug: CatalogSlug,
    owner: Username.meta({ description: "The persona that owns the item." }),
    details: z.record(z.string(), z.unknown()).meta({
      description:
        "The version's details, as its kind defines them, such as a picture's title and tags or an eval's cases.",
    }),
    files: z.record(CatalogFileRole, Sha256).meta({
      description: "Its files by role, each already put.",
      example: { manifest: "9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08" },
    }),
    label: z.string().min(1).max(40).optional().meta({ description: "A label for the version." }),
  })
  .meta({ id: "PublishCatalogVersionRequest", description: "A finished version of one item." });
export type PublishCatalogVersionRequest = z.infer<typeof PublishCatalogVersionRequest>;

export const CatalogProblem = z
  .strictObject({
    where: z.string().meta({ example: "cover, face 2, level 12, tile 40 / 17" }),
    message: z.string().meta({ example: "Id 40 isn't in cover's legend." }),
  })
  .meta({ id: "CatalogProblem", description: "One way a version isn't valid for its kind." });
export type CatalogProblem = z.infer<typeof CatalogProblem>;

/** The steps of a catalog job, in order. A running job is on one of them. */
export const CATALOG_JOB_STEPS = ["check_package", "publish"] as const;
export type CatalogJobStep = (typeof CATALOG_JOB_STEPS)[number];

export const CatalogJobParams = z
  .strictObject({
    jobId: z
      .string()
      .regex(/^package-[0-9a-f-]{36}$/)
      .meta({
        description: "The job's Workflow instance, named after the version it publishes.",
        example: "package-019a0f3c-7d22-7b19-8e40-c3a5d61f0b28",
      }),
  })
  .meta({ id: "CatalogJobParams" });

export const CatalogJob = z
  .strictObject({
    id: z.string().meta({ example: "package-019a0f3c-7d22-7b19-8e40-c3a5d61f0b28" }),
    status: z.enum(["queued", "running", "succeeded", "failed"]),
    step: z.enum(CATALOG_JOB_STEPS).nullable().meta({
      description:
        "The step a running job is on, which each step records as it begins, or null before the first and once it's done.",
    }),
    failure: z
      .strictObject({
        reason: z.enum(["package_invalid", "internal"]),
        message: z.string(),
        problems: z.array(CatalogProblem).meta({
          description: "Every problem `checkPackage` found, for a package that isn't a basemap.",
        }),
      })
      .nullable(),
    result: z
      .strictObject({ itemId: z.uuid(), versionId: z.uuid() })
      .nullable()
      .meta({ description: "The version it published, now the item's live version." }),
  })
  .meta({
    id: "CatalogJob",
    description:
      "A Cloudflare Workflow finishing a catalog version's publish, such as a basemap's.",
  });
export type CatalogJob = z.infer<typeof CatalogJob>;

export const PublishCatalogVersionResponse = z
  .strictObject({
    itemId: z.uuid(),
    versionId: z.uuid(),
    version: z
      .int()
      .positive()
      .optional()
      .meta({ description: "Its number, where its kind gives one." }),
    published: z.boolean().meta({
      description: "`false` when the item's live version already matched, so nothing was written.",
    }),
    job: CatalogJob.optional().meta({
      description:
        "The job still publishing it, for a basemap, whose package is checked a file at a time. The version is the item's live version once the job succeeds.",
    }),
  })
  .meta({ id: "PublishCatalogVersionResponse", description: "The item's live version." });
export type PublishCatalogVersionResponse = z.infer<typeof PublishCatalogVersionResponse>;

export const CatalogProblems = z
  .strictObject({ problems: z.array(CatalogProblem).min(1) })
  .meta({ id: "CatalogProblems", description: "Every problem the kind's validator found." });
export type CatalogProblems = z.infer<typeof CatalogProblems>;
